import React, { useState, useEffect } from "react";
import { fetchExams } from "@/services/exams.util";

const ExamAssignmentModal = ({ 
  isOpen, 
  onClose, 
  selectedUsers = [], 
  onAssignExams 
}) => {
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch available exams when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableExams();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedExams([]);
      setError('');
    }
  }, [isOpen]);

  const fetchAvailableExams = async () => {
    try {
      setLoading(true);
      setError('');
      const examData = await fetchExams();
      setExams(examData || []);
    } catch (err) {
      console.error('Error fetching exams:', err);
      setError('Failed to fetch available exams');
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExamSelection = (examId, isSelected) => {
    if (isSelected) {
      setSelectedExams(prev => [...prev, examId]);
    } else {
      setSelectedExams(prev => prev.filter(id => id !== examId));
    }
  };

  const handleSelectAllExams = (isSelected) => {
    if (isSelected) {
      setSelectedExams(exams.map(exam => exam._id));
    } else {
      setSelectedExams([]);
    }
  };

  const handleAssignExams = async () => {
    if (selectedExams.length === 0) {
      setError('Please select at least one exam to assign');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('No users selected for assignment');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      await onAssignExams(selectedUsers, selectedExams);
      onClose();
    } catch (err) {
      console.error('Error assigning exams:', err);
      setError(err.message || 'Failed to assign exams');
    } finally {
      setSubmitting(false);
    }
  };

  const isAllSelected = exams.length > 0 && selectedExams.length === exams.length;
  const isSomeSelected = selectedExams.length > 0 && selectedExams.length < exams.length;

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
    >
      <div className="modal-content bg-white rounded-16 shadow-4" style={{ width: '600px', maxHeight: '80vh' }}>
        {/* Header */}
        <div className="modal-header px-30 py-20 border-bottom-light">
          <div className="d-flex items-center justify-between">
            <h4 className="text-20 lh-1 fw-500">Assign Exams to Users</h4>
            <button 
              onClick={onClose}
              className="button -sm bg-light-4 text-light-1"
              disabled={submitting}
            >
              <i className="icon-close text-16"></i>
            </button>
          </div>
          <div className="text-15 text-light-1 mt-10">
            Assign exams to {selectedUsers.length} selected user{selectedUsers.length > 1 ? 's' : ''}
          </div>
        </div>

        {/* Content */}
        <div className="modal-body px-30 py-20" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {error && (
            <div className="alert alert-danger mb-20">
              {error}
            </div>
          )}

          {loading ? (
            <div className="d-flex justify-center items-center py-40">
              <div className="text-15">Loading exams...</div>
            </div>
          ) : exams.length === 0 ? (
            <div className="d-flex justify-center items-center py-40">
              <div className="text-15 text-light-1">No exams available for assignment</div>
            </div>
          ) : (
            <div>
              {/* Select All Checkbox */}
              <div className="d-flex items-center mb-20 pb-15 border-bottom-light">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isSomeSelected;
                  }}
                  onChange={(e) => handleSelectAllExams(e.target.checked)}
                  className="form-checkbox mr-15"
                />
                <label className="text-15 fw-500">
                  Select All ({exams.length} exams)
                </label>
              </div>

              {/* Exam List */}
              <div className="y-gap-15">
                {exams.map((exam) => (
                  <div key={exam._id} className="d-flex items-center p-15 bg-light-6 rounded-8">
                    <input
                      type="checkbox"
                      checked={selectedExams.includes(exam._id)}
                      onChange={(e) => handleExamSelection(exam._id, e.target.checked)}
                      className="form-checkbox mr-15"
                    />
                    <div className="flex-1">
                      <div className="d-flex items-center">
                        <div className="text-16 fw-500">{exam.title}</div>
                        {exam.isFree && (
                          <span className="badge bg-green-1 text-white ml-10 px-10 py-3 text-12">
                            FREE
                          </span>
                        )}
                        {!exam.isPurchasable && (
                          <span className="badge bg-orange-1 text-white ml-10 px-10 py-3 text-12">
                            NOT PURCHASABLE
                          </span>
                        )}
                      </div>
                      {exam.description && (
                        <div className="text-14 text-light-1 mt-5">
                          {exam.description}
                        </div>
                      )}
                      <div className="d-flex items-center mt-8 text-13 text-light-1">
                        {exam.price && (
                          <div className="mr-15">
                            Regular: ₹{exam.discountPrice || exam.price}
                            {exam.discountPrice && (
                              <span className="text-light-2 line-through ml-5">₹{exam.price}</span>
                            )}
                          </div>
                        )}
                        {exam.testsOnlyPrice && (
                          <div>
                            Tests Only: ₹{exam.testsOnlyDiscountPrice || exam.testsOnlyPrice}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer px-30 py-20 border-top-light">
          <div className="d-flex items-center justify-between">
            <div className="text-15 text-light-1">
              {selectedExams.length} exam{selectedExams.length !== 1 ? 's' : ''} selected
            </div>
            <div className="d-flex items-center x-gap-15">
              <button 
                onClick={onClose}
                className="button -sm bg-light-4 text-light-1 fw-500"
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                onClick={handleAssignExams}
                className="button -sm bg-purple-1 text-white fw-500"
                disabled={submitting || selectedExams.length === 0 || selectedUsers.length === 0}
              >
                {submitting ? 'Assigning...' : `Assign to ${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamAssignmentModal;
